import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, PlayCircle, X } from 'lucide-react';
import type { OnboardingProgress } from '@tailoring-crm/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tailoring-crm/ui';
import { useDismissGuidedTour } from '../../lib/onboarding/use-onboarding.js';
import { useTour } from './tour/tour-controller.js';

/**
 * The guided first order: three actions, in the order a shop actually does them, using the
 * shop's OWN customer rather than a sandbox.
 *
 * A sandbox would be safer and teach nothing. The point of the walkthrough is to reach a real
 * booked order — the moment after which a shop keeps coming back — so every step here links
 * into the real screen and the ticks come from the real data. When the third one ticks, the
 * card has done its job and stops appearing on its own.
 *
 * It is dismissible at any point, permanently and for the whole shop. Somebody who has done
 * this before must be able to make it go away in one tap, and it must not come back on the
 * next reload or on the manager's phone.
 */
export function GuidedTour({ progress }: { progress: OnboardingProgress }): ReactElement | null {
  const { t } = useTranslation();
  const dismiss = useDismissGuidedTour();
  const spotlight = useTour();

  const { tour } = progress;
  // Done, or waved away. Either way the shop is past this.
  if (tour.dismissed || tour.orderBooked) return null;

  const steps = [
    { key: 'customer', done: tour.customerAdded, to: '/customers' },
    { key: 'measurement', done: tour.measurementSaved, to: '/customers' },
    { key: 'order', done: tour.orderBooked, to: '/orders/new' },
  ] as const;

  // The first thing not yet done is the only one with a button on it: three competing calls to
  // action would leave the owner deciding where to start, which is the decision this removes.
  const next = steps.find((step) => !step.done);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>{t('onboarding.tour.title')}</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.tour.description')}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('onboarding.tour.dismiss')}
          disabled={dismiss.isPending}
          onClick={() => dismiss.mutate()}
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <ol className="flex flex-col gap-2">
          {steps.map((step, index) => (
            <li key={step.key} className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className={
                  step.done
                    ? 'flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    : 'flex size-6 shrink-0 items-center justify-center rounded-full border border-border text-xs text-muted-foreground'
                }
              >
                {step.done ? <Check className="size-3.5" /> : index + 1}
              </span>
              <span
                className={step.done ? 'text-sm text-muted-foreground line-through' : 'text-sm'}
              >
                {t(`onboarding.tour.${step.key}`)}
              </span>
            </li>
          ))}
        </ol>

        {next ? (
          <div className="flex flex-wrap gap-2">
            {/* The spotlight walks the same three steps, pointing at each control in turn. It
                is offered beside the plain link, not instead of it, for whoever would rather
                find their own way. Hidden while it is already running. */}
            {spotlight && spotlight.status !== 'running' ? (
              <Button onClick={spotlight.start}>
                <PlayCircle aria-hidden="true" />
                {t(
                  spotlight.status === 'paused'
                    ? 'onboarding.spotlight.resume'
                    : 'onboarding.spotlight.showMe',
                )}
              </Button>
            ) : null}
            <Button asChild variant={spotlight ? 'outline' : 'default'}>
              <Link to={next.to}>{t(`onboarding.tour.${next.key}Action`)}</Link>
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
